"use client";

import {
  animate,
  motion,
  transform,
  useMotionValue,
  useTransform,
} from "motion/react";
import { useCallback, useEffect, useId, useRef } from "react";

import {
  LOGO_INNER_STROKE,
  LOGO_OUTER_STROKE,
  LOGO_PATHS,
  LOGO_VIEWBOX,
  logoDrawRanges,
} from "@/components/shared/logo";
import { usePrefersReducedMotion } from "@/hooks/use-prefers-reduced-motion";
import { cn } from "@/lib/utils";

// =============================================================================

const DRAW_EASE = [0.65, 0, 0.35, 1] as const;
const RANGES = logoDrawRanges();

/** Drawn share of a path, 0 until the pen reaches it */
const share = (progress: number, [start, end]: [number, number]) =>
  transform(progress, [start, end], [0, 1]);

/** Hidden until the pen reaches it: its round cap would show as a dot */
const reached = (progress: number, [start]: [number, number]) =>
  progress > start ? 1 : 0;

/**
 * Logo of the navbar. It draws itself on load and plays a little trick when
 * its link is hovered or focused: the stem dives into the bowl and springs
 * back up while the "c" spins around like a loader before settling back.
 */
export function NavLogo({ className }: { className?: string }) {
  const id = useId().replace(/:/g, "");
  const shouldReduceMotion = usePrefersReducedMotion();
  const isPlaying = useRef(false);
  const svgRef = useRef<SVGSVGElement>(null);

  // 0 → 1 on load, one pen: the "b" in one stroke, then the "c" (drawn at
  // once when motion is reduced)
  const draw = useMotionValue(0);
  // Hover trick
  const dive = useMotionValue(0);
  const squash = useMotionValue(0);
  const spin = useMotionValue(0);
  const scale = useMotionValue(1);

  const stemLength = useTransform([draw, dive], ([d, v]: number[]) =>
    Math.max(0, share(d, RANGES.stemAndBowl) - v)
  );
  const stemOpacity = useTransform(draw, (d) => reached(d, RANGES.stemAndBowl));
  const bowlLength = useTransform(draw, (d) => share(d, RANGES.bowlTop));
  const bowlOpacity = useTransform(
    [draw, dive],
    ([d, v]: number[]) =>
      reached(d, RANGES.bowlTop) * transform(v, [0, 0.4], [1, 0.25])
  );
  const innerTopLength = useTransform([draw, squash], ([d, s]: number[]) =>
    Math.max(0.001, share(d, RANGES.innerTop) * (1 - s))
  );
  const innerTopOpacity = useTransform(draw, (d) =>
    reached(d, RANGES.innerTop)
  );
  const innerBottomLength = useTransform([draw, squash], ([d, s]: number[]) =>
    Math.max(0.001, share(d, RANGES.innerBottom) * (1 - s))
  );
  const innerBottomOpacity = useTransform(draw, (d) =>
    reached(d, RANGES.innerBottom)
  );

  useEffect(() => {
    if (shouldReduceMotion) {
      draw.set(1);
      return;
    }
    const controls = animate(draw, 1, {
      duration: 1.5,
      delay: 0.2,
      ease: DRAW_EASE,
    });
    return () => controls.stop();
  }, [draw, shouldReduceMotion]);

  const play = useCallback(async () => {
    if (shouldReduceMotion || isPlaying.current || draw.get() < 1) return;
    isPlaying.current = true;
    spin.set(0);
    await Promise.all([
      // The stem dives into the bowl, then springs back up
      animate(dive, [0, 0.42, 0], {
        duration: 0.95,
        times: [0, 0.38, 1],
        ease: ["easeIn", "backOut"],
      }),
      // The "c" turns into a short arc spinning like a loader
      animate(squash, [0, 0.72, 0.72, 0], {
        duration: 1.05,
        times: [0, 0.3, 0.62, 1],
        ease: ["easeOut", "linear", "easeInOut"],
      }),
      animate(spin, 360, { duration: 1.05, ease: [0.45, 0, 0.2, 1] }),
      animate(scale, [1, 0.9, 1.06, 1], {
        duration: 0.95,
        times: [0, 0.35, 0.7, 1],
        ease: "easeInOut",
      }),
    ]);
    spin.set(0);
    isPlaying.current = false;
  }, [dive, draw, scale, shouldReduceMotion, spin, squash]);

  // Triggered by the whole link (padding included) and by keyboard focus
  useEffect(() => {
    const target = svgRef.current?.closest("a") ?? svgRef.current;
    if (!target) return;
    const onEnter = () => void play();
    target.addEventListener("pointerenter", onEnter);
    target.addEventListener("focus", onEnter);
    return () => {
      target.removeEventListener("pointerenter", onEnter);
      target.removeEventListener("focus", onEnter);
    };
  }, [play]);

  const bowlGradient = `nav-logo-bowl-${id}`;
  const innerGradient = `nav-logo-inner-${id}`;

  return (
    <motion.svg
      ref={svgRef}
      viewBox={LOGO_VIEWBOX}
      fill="none"
      strokeLinecap="round"
      className={cn("h-auto overflow-visible", className)}
      style={{ scale }}
      aria-hidden
    >
      <defs>
        <linearGradient
          id={bowlGradient}
          x1="256.426"
          y1="211.165"
          x2="351.413"
          y2="293.456"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="currentColor" stopOpacity="0" />
          <stop offset="1" stopColor="currentColor" />
        </linearGradient>
        <linearGradient
          id={innerGradient}
          x1="297.59"
          y1="347.798"
          x2="217.085"
          y2="347.881"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="currentColor" stopOpacity="0" />
          <stop offset="1" stopColor="currentColor" />
        </linearGradient>
      </defs>

      <g stroke="currentColor" strokeWidth={LOGO_OUTER_STROKE}>
        <motion.path
          d={LOGO_PATHS.stemAndBowl}
          style={{
            pathLength: stemLength,
            pathOffset: dive,
            opacity: stemOpacity,
          }}
        />
        <motion.path
          d={LOGO_PATHS.bowlTop}
          stroke={`url(#${bowlGradient})`}
          style={{ pathLength: bowlLength, opacity: bowlOpacity }}
        />
      </g>

      {/* The "c" turns around the center of the bowl */}
      <motion.g
        stroke="currentColor"
        strokeWidth={LOGO_INNER_STROKE}
        style={{ rotate: spin, originX: 0.553, originY: 0.5 }}
      >
        <motion.path
          d={LOGO_PATHS.innerTop}
          style={{ pathLength: innerTopLength, opacity: innerTopOpacity }}
        />
        <motion.path
          d={LOGO_PATHS.innerBottom}
          stroke={`url(#${innerGradient})`}
          style={{ pathLength: innerBottomLength, opacity: innerBottomOpacity }}
        />
      </motion.g>
    </motion.svg>
  );
}

// =============================================================================
